import { useState, useEffect, useRef } from "react";
import { sqlRead, sqlEscape } from "../utils/sqlClient";
import { CostCodePicker, AddCostCodeFlyout } from "../components/CostCodePicker";

import { useIsAdmin, AdminGate } from "../auth/AdminContext";

// Quote Log Setup — master Category/Subcategory picklist (QuoteCategoryOptions,
// db/33_quote_categories.sql) and the Category/Subcategory → unit-cost metric
// associations (QuoteUnitMetrics, db/32_quote_unit_metrics.sql) used across
// quote intake and search, editable here instead of hardcoded in source.
// Consumed by src/pages/QuoteSearch.jsx.
//
// CostCodes is the single source of truth for CSI structure: every Category
// row points at a real CostCodes row via CostCodeID (picked through the
// search combobox below, not free-typed), and sorts by that code. A
// Subcategory never carries its own code — it's just a divisor splitting up
// quotes under its parent Category's one code (enforced in the DB by
// CK_QuoteCategoryOptions_CodeMatchesLevel in db/33_quote_categories.sql).
//
// Laid out as a tree — Category → Subcategories → Unit Cost Metrics — each
// level collapsible, with a count badge so an empty branch (no subcategories
// yet, or a subcategory with no metrics configured) is obvious without having
// to open it.
//
// Edit workflow: the page loads read-only (plain aligned text, no stray inputs
// to accidentally edit). "Edit" unlocks every field, add, and delete action —
// all of which stay purely local (nothing hits the database) until "Save
// Changes" is clicked, which persists everything at once; "Cancel" discards
// and reloads from the server instead.
//
// DisplayOrder is a manual sort key, stepped by 10 (10, 20, 30…) so a new row
// can be inserted between two existing ones (e.g. 25 between 20 and 30)
// without renumbering the rest. If two neighbors ever end up adjacent (20,
// 21 — no gap left), hit "Renumber" on that list to reset clean 10-steps
// while preserving the current order.

const ctl = { padding: "6px 8px", fontSize: 12.5, border: "1px solid var(--cs-border)", borderRadius: 6, background: "var(--surface)", color: "var(--text)", width: "100%", boxSizing: "border-box" };

// True once any two neighbors in DisplayOrder order are 1 apart or less — i.e.
// there's no integer left to insert a new row between them. Renumber is only
// worth showing at that point; otherwise it's just clutter suggesting action
// is needed when the current spacing is still fine.
function needsRenumber(list) {
  const sorted = [...list].sort((a, b) => a.DisplayOrder - b.DisplayOrder);
  return sorted.some((r, i) => i > 0 && r.DisplayOrder - sorted[i - 1].DisplayOrder <= 1);
}
const CAT_GRID    = "22px minmax(160px,1fr) 90px 140px 150px 28px";
const SUBCAT_GRID = "22px minmax(140px,1fr) 56px 120px 28px";
const METRIC_GRID = "minmax(120px,1fr) minmax(140px,1fr) 90px 56px 120px 28px";

function fmtUnitCost(n) {
  const v = +n;
  if (!v) return "—";
  return "$" + v.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

// Categories sort by their real CSI code, not a manual number — editing the
// code is what moves a category's position. No code (e.g. "Other") sorts
// last; two categories sharing the same code (e.g. two under 13 21 13) just
// tie-break alphabetically, since which one comes first genuinely doesn't
// matter.
function sortByCSI(rows) {
  return [...rows].sort((a, b) => {
    const an = a.CSICodeNumeric || "", bn = b.CSICodeNumeric || "";
    if (!an && !bn) return a.Category.localeCompare(b.Category);
    if (!an) return 1;
    if (!bn) return -1;
    return an === bn ? a.Category.localeCompare(b.Category) : an.localeCompare(bn);
  });
}

function Field({ editing, value, onCommit, type = "text", placeholder, mono, align }) {
  if (!editing) {
    return (
      <div style={{
        padding: "6px 8px", fontSize: 12.5, color: value ? "var(--text)" : "var(--text3)",
        fontFamily: mono ? "monospace" : undefined, whiteSpace: "nowrap", overflow: "hidden",
        textOverflow: "ellipsis", textAlign: align || "left",
      }}>
        {value ?? "—"}
      </div>
    );
  }
  return (
    <input
      defaultValue={value ?? ""}
      type={type}
      placeholder={placeholder}
      style={{ ...ctl, fontFamily: mono ? "monospace" : undefined, textAlign: align || "left" }}
      onBlur={e => {
        const val = type === "number" ? (+e.target.value || 0) : e.target.value;
        if (val !== (value ?? (type === "number" ? 0 : ""))) onCommit(val);
      }}
    />
  );
}

function DeleteButton({ onClick, title }) {
  return (
    <button
      onClick={onClick}
      title={title || "Remove"}
      style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#d1d5db", flexShrink: 0 }}
      onMouseEnter={e => e.currentTarget.style.color = "#dc2626"}
      onMouseLeave={e => e.currentTarget.style.color = "#d1d5db"}
    >🗑</button>
  );
}

function AddButton({ onClick, children, small }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: small ? "4px 10px" : "6px 14px", fontSize: small ? 11 : 12,
        fontWeight: 700, background: "var(--surface)", color: "var(--cs-blue)",
        border: "1px dashed var(--cs-blue)", borderRadius: 6, cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

function RenumberButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      title="Reset to clean 10-step spacing, keeping current order"
      style={{
        padding: "4px 10px", fontSize: 11, fontWeight: 600, background: "none",
        color: "var(--text3)", border: "1px solid var(--cs-border)", borderRadius: 6, cursor: "pointer",
      }}
    >↺ Renumber</button>
  );
}

function Triangle({ open, onClick }) {
  return (
    <button
      onClick={onClick}
      aria-label={open ? "Collapse" : "Expand"}
      style={{
        background: "none", border: "none", cursor: "pointer", padding: 4,
        fontSize: 10, color: "var(--text3)", display: "flex", alignItems: "center", justifyContent: "center",
        transform: open ? "rotate(90deg)" : "none", transition: "transform 0.12s",
      }}
    >▸</button>
  );
}

function CountBadge({ count, singular, plural }) {
  const empty = count === 0;
  return (
    <span style={{
      fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 100,
      background: empty ? "#fef3c7" : "#eef2ff", color: empty ? "#92400e" : "#3730a3",
      whiteSpace: "nowrap", justifySelf: "start",
    }}>
      {empty ? `No ${plural}` : `${count} ${count === 1 ? singular : plural}`}
    </span>
  );
}

function GridHeader({ template, labels }) {
  return (
    <div style={{
      display: "grid", gridTemplateColumns: template, gap: 8, alignItems: "center",
      padding: "4px 6px", fontSize: 10, fontWeight: 700, color: "var(--text3)",
      textTransform: "uppercase", letterSpacing: "0.04em",
    }}>
      {labels.map((l, i) => <span key={i}>{l}</span>)}
    </div>
  );
}

function MetricRow({ row, editing, onUpdate, onRemove, stat }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: METRIC_GRID, gap: 8, alignItems: "center", marginBottom: 6 }}>
      <Field editing={editing} value={row.MetricLabel}   onCommit={v => onUpdate({ MetricLabel: v })}   placeholder="Metric Label" />
      <Field editing={editing} value={row.AttributeName} onCommit={v => onUpdate({ AttributeName: v })} placeholder="Attribute Name(s), comma-separated" />
      <Field editing={editing} value={row.UnitLabel}      onCommit={v => onUpdate({ UnitLabel: v })}     placeholder="Unit Label" />
      <Field editing={editing} value={row.DisplayOrder}  onCommit={v => onUpdate({ DisplayOrder: v })}  type="number" align="right" />
      <div style={{ fontSize: 11, color: stat ? "var(--text2)" : "var(--text3)", textAlign: "right" }} title="Stored, from QuoteUnitCostSnapshots — recomputed whenever a quote or this metric changes">
        {stat ? `avg ${fmtUnitCost(stat.AvgUnitCost)} · ${stat.QuoteCount} quote${stat.QuoteCount === 1 ? "" : "s"}` : "no history yet"}
      </div>
      {editing ? <DeleteButton onClick={onRemove} /> : <span />}
    </div>
  );
}

function MetricsBranch({ metrics, editing, onAddMetric, onUpdateMetric, onRemoveMetric, onRenumber, statsByMetricId }) {
  return (
    <div style={{ marginLeft: 20, paddingLeft: 12, borderLeft: "2px solid var(--cs-border)", marginBottom: 8 }}>
      {metrics.length === 0 && (
        <div style={{ fontSize: 11.5, color: "var(--text3)", padding: "4px 0 2px" }}>No unit-cost metrics yet.</div>
      )}
      {metrics.length > 0 && (
        <GridHeader template={METRIC_GRID} labels={["Metric Label", "Attribute Name(s)", "Unit Label", "Order", "History", ""]} />
      )}
      {metrics.map((m, i) => (
        <MetricRow
          key={m.MetricID ?? m._tempKey ?? i}
          row={m}
          editing={editing}
          onUpdate={patch => onUpdateMetric(m, patch)}
          onRemove={() => onRemoveMetric(m)}
          stat={m.MetricID ? statsByMetricId?.get(m.MetricID) : null}
        />
      ))}
      {editing && (
        <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
          <AddButton small onClick={onAddMetric}>+ Add Metric</AddButton>
          {needsRenumber(metrics) && <RenumberButton onClick={onRenumber} />}
        </div>
      )}
    </div>
  );
}

export default function QuoteLogSetup() {
  const isAdmin = useIsAdmin();

  const [catOptions, setCatOptions] = useState([]);
  const [metrics, setMetrics]       = useState([]);
  const [costCodes, setCostCodes]   = useState([]);
  const [metricStats, setMetricStats] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState("");

  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [pendingDeletes, setPendingDeletes] = useState([]); // [{ kind: 'option'|'metric', id }]
  const [showAddCostCode, setShowAddCostCode] = useState(false);
  const tempIdRef = useRef(0);
  const nextTempKey = () => (tempIdRef.current -= 1);

  const [openCats, setOpenCats]       = useState(new Set());
  const [openSubcats, setOpenSubcats] = useState(new Set());

  const load = () => {
    setLoading(true);
    setError("");
    return Promise.all([
      sqlRead("EXEC sp_GetQuoteCategoryOptions"),
      sqlRead("EXEC sp_GetQuoteUnitMetrics"),
      sqlRead("EXEC sp_GetCostCodes"),
      sqlRead("EXEC sp_GetUnitCostMetricStats").catch(() => []),
    ])
      .then(([cats, mets, codes, stats]) => { setCatOptions(cats); setMetrics(mets); setCostCodes(codes); setMetricStats(stats); })
      .catch(e => setError(e.message || String(e)))
      .finally(() => setLoading(false));
  };
  // NOT useEffect(load, []) — load() returns a Promise (it's awaited from
  // handleSaveAll), and an effect that returns anything other than
  // undefined/a function is treated as a cleanup function. React's production
  // build doesn't type-check before invoking it on unmount (only dev warns),
  // so navigating away from this page called that Promise as a function and
  // threw "TypeError: [x] is not a function" deep in React's commit phase —
  // the exact reported blank-page bug. Wrapping in a block body discards the
  // Promise so the effect returns nothing, while load() itself is untouched.
  useEffect(() => { load(); }, []);

  const toggleCat = name => setOpenCats(prev => {
    const next = new Set(prev);
    next.has(name) ? next.delete(name) : next.add(name);
    return next;
  });
  const toggleSubcat = key => setOpenSubcats(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  // ── Category / Subcategory options ──────────────────────────────────────────
  const categoryRows  = sortByCSI(catOptions.filter(r => !r.Subcategory));
  const subcategories = catOptions.filter(r => !!r.Subcategory);
  const statsByMetricId = new Map(metricStats.map(s => [s.MetricID, s]));

  function updateOption(row, patch) {
    setCatOptions(prev => prev.map(r => r === row ? { ...r, ...patch } : r));
  }

  function addCategory() {
    const draft = { OptionID: null, _tempKey: nextTempKey(), Category: "New Category", Subcategory: null, DisplayOrder: 100, CostCodeID: null, CSICode: null, CSICodeNumeric: null };
    setCatOptions(prev => [...prev, draft]);
    setOpenCats(prev => new Set(prev).add(draft.Category));
  }

  function addSubcategory(category) {
    const existing = subcategories.filter(s => s.Category === category);
    const draft = { OptionID: null, _tempKey: nextTempKey(), Category: category, Subcategory: "New Subcategory", DisplayOrder: (existing.length + 1) * 10 };
    setCatOptions(prev => [...prev, draft]);
    setOpenSubcats(prev => new Set(prev).add(`${category}::${draft.Subcategory}`));
  }

  function removeOption(row, label) {
    if (!window.confirm(`Remove ${label}?`)) return;
    setCatOptions(prev => prev.filter(r => r !== row));
    if (row.OptionID) setPendingDeletes(prev => [...prev, { kind: "option", id: row.OptionID }]);
  }

  function renumberSubcategories(category) {
    const sorted = subcategories.filter(s => s.Category === category).sort((a, b) => a.DisplayOrder - b.DisplayOrder);
    const next = new Map(sorted.map((r, i) => [r, (i + 1) * 10]));
    setCatOptions(prev => prev.map(r => next.has(r) ? { ...r, DisplayOrder: next.get(r) } : r));
  }

  // ── Unit-cost metrics ────────────────────────────────────────────────────────
  function updateMetric(row, patch) {
    setMetrics(prev => prev.map(r => r === row ? { ...r, ...patch } : r));
  }

  function addMetric(category, subcategory) {
    const draft = {
      MetricID: null, _tempKey: nextTempKey(), Category: category, Subcategory: subcategory || null,
      MetricLabel: "Cost / New", AttributeName: "Attribute Name", UnitLabel: "",
      DisplayOrder: 100,
    };
    setMetrics(prev => [...prev, draft]);
  }

  function removeMetric(row) {
    if (!window.confirm(`Remove "${row.MetricLabel}" for ${row.Category}${row.Subcategory ? " / " + row.Subcategory : ""}?`)) return;
    setMetrics(prev => prev.filter(r => r !== row));
    if (row.MetricID) setPendingDeletes(prev => [...prev, { kind: "metric", id: row.MetricID }]);
  }

  function renumberMetrics(list) {
    const sorted = [...list].sort((a, b) => a.DisplayOrder - b.DisplayOrder);
    const next = new Map(sorted.map((r, i) => [r, (i + 1) * 10]));
    setMetrics(prev => prev.map(r => next.has(r) ? { ...r, DisplayOrder: next.get(r) } : r));
  }

  // ── Save / Cancel ────────────────────────────────────────────────────────────
  async function saveOptionRow(row) {
    const payload = {
      option_id:      row.OptionID ?? null,
      category:       row.Category,
      subcategory:    row.Subcategory || null,
      display_order:  row.DisplayOrder ?? 100,
      cost_code_id:   row.CostCodeID ?? null,
    };
    return sqlRead(`EXEC sp_SaveQuoteCategoryOption @payload='${sqlEscape(JSON.stringify(payload))}'`);
  }

  async function saveMetricRow(row) {
    const payload = {
      metric_id:      row.MetricID ?? null,
      category:       row.Category,
      subcategory:    row.Subcategory || null,
      metric_label:   row.MetricLabel,
      attribute_name: row.AttributeName,
      unit_label:     row.UnitLabel || null,
      display_order:  row.DisplayOrder ?? 100,
    };
    return sqlRead(`EXEC sp_SaveQuoteUnitMetric @payload='${sqlEscape(JSON.stringify(payload))}'`);
  }

  async function handleSaveAll() {
    setSaving(true);
    try {
      for (const d of pendingDeletes) {
        if (d.kind === "option") await sqlRead(`EXEC sp_DeleteQuoteCategoryOption @payload='${sqlEscape(JSON.stringify({ option_id: d.id }))}'`);
        else await sqlRead(`EXEC sp_DeleteQuoteUnitMetric @payload='${sqlEscape(JSON.stringify({ metric_id: d.id }))}'`);
      }
      for (const row of catOptions) {
        const result = await saveOptionRow(row);
        if (!result?.[0]?.Success) {
          const where = row.Subcategory ? `${row.Category} / ${row.Subcategory}` : row.Category;
          throw new Error(`${where}: ${result?.[0]?.ErrorMessage || "save failed"}`);
        }
      }
      for (const row of metrics) await saveMetricRow(row);
      setPendingDeletes([]);
      await load();
      setEditMode(false);
    } catch (e) {
      alert("Error saving changes: " + e.message);
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    setPendingDeletes([]);
    load();
    setEditMode(false);
  }

  if (isAdmin !== true) return <AdminGate isAdmin={isAdmin} />;
  if (loading) return <div style={{ padding: 32, color: "var(--text3)", fontSize: 13 }}>Loading…</div>;
  if (error) return <div style={{ padding: 32, color: "var(--red)", fontSize: 13 }}>{error}</div>;

  return (
    <>
      {showAddCostCode && (
        <AddCostCodeFlyout
          onClose={() => setShowAddCostCode(false)}
          onSaved={cc => setCostCodes(prev => [...prev, cc])}
        />
      )}
      <div style={{ padding: 32, maxWidth: 1080 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
        <div>
          <h2 style={{ margin: "0 0 4px", fontSize: 17, fontWeight: 700, color: "var(--text)" }}>Quote Log Setup</h2>
          <p style={{ margin: "0 0 8px", fontSize: 13, color: "var(--text3)" }}>
            Master Category/Subcategory picklist and unit-cost metric associations used across quote intake and
            search — edit here instead of a source code change.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0, paddingTop: 2 }}>
          {!editMode ? (
            <button onClick={() => setEditMode(true)} style={{
              padding: "7px 16px", fontSize: 12.5, fontWeight: 700, background: "var(--cs-blue)",
              color: "#fff", border: "none", borderRadius: 6, cursor: "pointer",
            }}>✏️ Edit</button>
          ) : (
            <>
              <button onClick={handleCancel} disabled={saving} style={{
                padding: "7px 16px", fontSize: 12.5, fontWeight: 600, background: "none",
                color: "var(--text2)", border: "1px solid var(--cs-border)", borderRadius: 6,
                cursor: saving ? "default" : "pointer",
              }}>Cancel</button>
              <button onClick={handleSaveAll} disabled={saving} style={{
                padding: "7px 16px", fontSize: 12.5, fontWeight: 700,
                background: saving ? "var(--cs-border)" : "var(--cs-blue)", color: "#fff",
                border: "none", borderRadius: 6, cursor: saving ? "default" : "pointer",
              }}>{saving ? "Saving…" : "💾 Save Changes"}</button>
            </>
          )}
        </div>
      </div>

      <p style={{ margin: "0 0 20px", fontSize: 12, color: "var(--text3)" }}>
        Every Category points at a real cost code, picked by searching the CostCodes list below — Categories sort by
        that code automatically, and two sharing a code just tie-break alphabetically. Subcategories never carry
        their own code; they're just a divisor splitting up quotes under their parent Category's one code. Expand a
        Category to manage its Subcategories, and expand a Subcategory to manage the "Cost / X" metrics Quote Search
        shows for it. The badge on each row shows how many child rows exist — an amber "No…" badge means that branch
        is still empty. Subcategories and metrics keep a simple manual Order (stepped by 10, so you can insert one
        between two others) since their position within a category doesn't need to track anything external —
        "↺ Renumber" resets clean spacing if two ever end up adjacent with no gap left. Attribute Name accepts
        comma-separated aliases (e.g. "Unit CFM, Airflow") when different vendors' quotes use different wording for
        the same thing — editing a metric's aliases here recomputes its stored history across every quote in that
        category. "History" shows the average unit cost across every quote that resolved this metric — stored in
        QuoteUnitCostSnapshots and recomputed whenever a quote or this metric changes, not calculated on the fly.
        Click "Edit" to change anything; nothing is saved until you click "Save Changes".
      </p>

      <div style={{ border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", background: "#fff", padding: "6px 4px", overflowX: "auto" }}>
        <GridHeader template={CAT_GRID} labels={["", "Category", "CSI Code", "Subcategories", "Category Metrics", ""]} />
        {categoryRows.length === 0 && (
          <div style={{ padding: 20, textAlign: "center", color: "var(--text3)", fontSize: 13 }}>No categories yet.</div>
        )}
        {categoryRows.map((cat, ci) => {
          const catOpen = openCats.has(cat.Category);
          const subs = subcategories.filter(s => s.Category === cat.Category).sort((a, b) => a.DisplayOrder - b.DisplayOrder);
          const catMetrics = metrics.filter(m => m.Category === cat.Category && !m.Subcategory);

          return (
            <div key={cat.OptionID ?? cat._tempKey ?? ci} style={{ borderTop: ci > 0 ? "1px solid var(--cs-border)" : "none", padding: "6px 6px" }}>
              {/* Category row */}
              <div style={{ display: "grid", gridTemplateColumns: CAT_GRID, gap: 8, alignItems: "center" }}>
                <Triangle open={catOpen} onClick={() => toggleCat(cat.Category)} />
                <Field editing={editMode} value={cat.Category} onCommit={v => updateOption(cat, { Category: v })} />
                {editMode ? (
                  <CostCodePicker
                    value={cat.CostCodeID}
                    costCodes={costCodes}
                    onSelect={cc => updateOption(cat, {
                      CostCodeID: cc.CostCodeID, CSICode: cc.CSICode,
                      CSICodeNumeric: cc.CSICodeNumeric, Division: cc.Division,
                    })}
                  />
                ) : (
                  <Field editing={false} value={cat.CSICode} mono />
                )}
                <CountBadge count={subs.length} singular="subcategory" plural="subcategories" />
                <CountBadge count={catMetrics.length} singular="category metric" plural="category metrics" />
                {editMode ? <DeleteButton onClick={() => removeOption(cat, cat.Category)} /> : <span />}
              </div>

              {/* Category children: category-level metrics + subcategories */}
              {catOpen && (
                <div style={{ marginTop: 8, marginLeft: 20, paddingLeft: 12, borderLeft: "2px solid var(--cs-border)" }}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>
                    Category-wide metrics
                  </div>
                  <MetricsBranch
                    metrics={catMetrics}
                    editing={editMode}
                    onAddMetric={() => addMetric(cat.Category, null)}
                    onUpdateMetric={(row, patch) => updateMetric(row, patch)}
                    onRemoveMetric={row => removeMetric(row)}
                    onRenumber={() => renumberMetrics(catMetrics)}
                    statsByMetricId={statsByMetricId}
                  />

                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 4, marginBottom: 6 }}>
                    <div style={{ fontSize: 10.5, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      Subcategories
                    </div>
                  </div>
                  {subs.length === 0 && (
                    <div style={{ fontSize: 11.5, color: "var(--text3)", padding: "2px 0 6px" }}>No subcategories yet.</div>
                  )}
                  {subs.length > 0 && (
                    <GridHeader template={SUBCAT_GRID} labels={["", "Subcategory", "Order", "Metrics", ""]} />
                  )}
                  {subs.map((sub, si) => {
                    const subKey = `${cat.Category}::${sub.Subcategory}`;
                    const subOpen = openSubcats.has(subKey);
                    const subMetrics = metrics.filter(m => m.Category === cat.Category && m.Subcategory === sub.Subcategory);
                    return (
                      <div key={sub.OptionID ?? sub._tempKey ?? si} style={{ marginBottom: 6 }}>
                        <div style={{ display: "grid", gridTemplateColumns: SUBCAT_GRID, gap: 8, alignItems: "center" }}>
                          <Triangle open={subOpen} onClick={() => toggleSubcat(subKey)} />
                          <Field editing={editMode} value={sub.Subcategory} onCommit={v => updateOption(sub, { Subcategory: v })} />
                          <Field editing={editMode} value={sub.DisplayOrder} type="number" align="right" onCommit={v => updateOption(sub, { DisplayOrder: v })} />
                          <CountBadge count={subMetrics.length} singular="metric" plural="metrics" />
                          {editMode ? <DeleteButton onClick={() => removeOption(sub, `${sub.Category} / ${sub.Subcategory}`)} /> : <span />}
                        </div>
                        {subOpen && (
                          <MetricsBranch
                            metrics={subMetrics}
                            editing={editMode}
                            onAddMetric={() => addMetric(cat.Category, sub.Subcategory)}
                            onUpdateMetric={(row, patch) => updateMetric(row, patch)}
                            onRemoveMetric={row => removeMetric(row)}
                            onRenumber={() => renumberMetrics(subMetrics)}
                            statsByMetricId={statsByMetricId}
                          />
                        )}
                      </div>
                    );
                  })}
                  {editMode && (
                    <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                      <AddButton small onClick={() => addSubcategory(cat.Category)}>+ Add Subcategory</AddButton>
                      {needsRenumber(subs) && <RenumberButton onClick={() => renumberSubcategories(cat.Category)} />}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {editMode && (
        <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
          <AddButton onClick={addCategory}>+ Add Category</AddButton>
          <AddButton onClick={() => setShowAddCostCode(true)}>+ Add Cost Code</AddButton>
        </div>
      )}
      </div>
    </>
  );
}
